import { AppDispatch } from "state/store";
import { openIframeModal } from "state/reducers/layoutSlice";
import { MCAT_TEST_TYPE } from "utils/constants";
import { decodeToken } from "./decodeToken";
import { isScoreReport } from "./isScoreReport";

/** How the URL was opened; callers may close local UI only for `newTab`. */
export type OpenExternalUrlResult = "iframe" | "newTab" | "skipped";

interface OpenTestOrDrillOptions {
  url: string | null | undefined;
  title?: string;
  contentObjectType?: string;
  testType?: string;
  dispatch: AppDispatch;
  onRefreshModalOpen?: (contentType?: string) => void;
}

export const isMCATExternalContent = (contentObjectType?: string): boolean => {
  return (
    contentObjectType === "AAMC" ||
    contentObjectType === "Articulate" ||
    contentObjectType === "Video"
  );
};

/**
 * Opens a test or drill URL in an iframe modal for MCAT, or in a new tab for other test types.
 * Also handles refresh modal if callback is provided.
 * 
 * @param options - Configuration object
 * @param options.url - The LaunchUrl to open
 * @param options.title - Optional title for the iframe modal (defaults to contentObjectType or "Test")
 * @param options.contentObjectType - The type of content (e.g., "Test", "Drill", "Practice Test")
 * @param options.testType - The test type from token (e.g., "MCAT", "ACT")
 * @param options.dispatch - Redux dispatch function
 * @param options.onRefreshModalOpen - Optional callback to open refresh modal after launching
 */
export const openExternalUrl = ({
  url,
  title,
  contentObjectType,
  testType,
  dispatch,
  onRefreshModalOpen,
}: OpenTestOrDrillOptions): OpenExternalUrlResult => {
  // Return early if no URL
  if (!url) {
    return "skipped";
  }

  // Get test type from token if not provided
  const testtype = testType || (() => {
    const token = decodeToken();
    return token?.testtype;
  })();

  const token = decodeToken();
  const isMCAT = testtype === MCAT_TEST_TYPE;
  const isAiEnabled =
    !!token &&
    typeof token.showaitutor === "string" &&
    token.showaitutor.toLowerCase() === "true";

  // Generate title if not provided
  const modalTitle = title || contentObjectType || "Test";

  // Use iframe only when MCAT and AI is enabled or is MCAT internal score report; otherwise open a new tab
  if (
    (isMCAT && isAiEnabled && !isMCATExternalContent(contentObjectType)) ||
    (isMCAT &&
      isScoreReport(url, modalTitle) &&
      !isMCATExternalContent(contentObjectType))
  ) {
    dispatch(
      openIframeModal({
        url,
        title: modalTitle,
      })
    );
    return "iframe";
  }

  window.open(url, "__blank");
  if (onRefreshModalOpen) {
    onRefreshModalOpen(contentObjectType || "Test");
  }
  return "newTab";
};
